"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileCheck2, MapPin, Navigation, Clock3, ArrowRight, ShieldCheck, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const capabilities = [{
  icon: FileCheck2,
  tag: "Permits & Compliance",
  title: "Council highway licensing",
  description: "We handle pavement licenses, parking bay suspensions, and highway permits directly with local authorities across Berkshire, Surrey, Hampshire, Buckinghamshire, and London.",
  points: ["Full paperwork and highway permit applications handled", "Pedestrian gantry protection and tunnel fans", "Compliance managed to borough-specific rules"]
}, {
  icon: Building2,
  tag: "Logistics & Site Access",
  title: "Urban access and tight footprints",
  description: "Navigating red routes, narrow mews, high-traffic commercial zones, and congested town centers without holding up your timeline or compromising pedestrian safety.",
  points: ["Restricted-access scaffolding for tight footprints", "Flexible delivery scheduling to suit site access windows", "Engineered structural towers and bridge designs"]
}, {
  icon: Navigation,
  tag: "Strategic Coverage",
  title: "Fast Home Counties response",
  description: "Positioned along key South East arterial corridors (M4, M3, M25, M40) for swift mobilization, scheduled site handovers, and regular 7-day statutory safety inspections.",
  points: ["Rapid dispatch across all covered county borders", "Responsive support when site conditions change.", "Dedicated contracts manager assigned per project"]
}];
export default function Expertise() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.075
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="local-expertise" data-nav="light" className="bg-background text-foreground py-16 md:py-24 lg:py-28 border-b border-border transition-colors relative">
      <div className="container mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-0.5 w-6 bg-primary" aria-hidden="true" />
            <span className="text-xs md:text-sm font-bold uppercase tracking-wider text-muted-foreground">
              Regional Operations & Coverage
            </span>
          </div>
          
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4 font-default">
            Local knowledge across the Home Counties
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            From council permits and pavement licenses to tight urban staging, we keep sites moving safely across Berkshire, Surrey, Hampshire, Buckinghamshire, and London.
          </p>
        </div>

        {/* Capability Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-12 md:mb-16">
          {capabilities.map((cap, i) => {
          const Icon = cap.icon;
          return <motion.div key={cap.title} data-index={i} variants={itemVariants} className="bg-card text-card-foreground rounded-lg border border-border p-6 sm:p-8 flex flex-col justify-between shadow-xs hover:border-primary/50 hover:shadow-md transition-[border-color,box-shadow] duration-200 group">
                <div>
                  <div className="flex items-center justify-between gap-4 mb-6">
                    <div className="p-3 rounded-md bg-accent text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
                      <Icon className="size-6 shrink-0" />
                    </div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2.5 py-1 rounded bg-muted">
                      {cap.tag}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-foreground mb-3 font-default">
                    {cap.title}
                  </h3>

                  <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                    {cap.description}
                  </p>

                  <ul className="space-y-2.5 pt-4 border-t border-border/70 text-sm text-foreground/90 mb-6">
                    {cap.points.map((point, ptIdx) => <li key={ptIdx} className="flex items-start gap-2.5">
                        <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                        <span className="text-xs sm:text-sm text-foreground/80">{point}</span>
                      </li>)}
                  </ul>
                </div>

                <div className="pt-2">
                  <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors group/link">
                    <span>Check coverage for your project</span>
                    <ArrowRight className="size-4 transition-transform group-hover/link:translate-x-1" />
                  </Link>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Trust & Action Strip */}
        <div className="rounded-lg bg-secondary text-secondary-foreground p-6 sm:p-8 border border-secondary">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-md bg-white/10 text-primary shrink-0">
                <MapPin className="size-6 text-primary" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-secondary-foreground font-default mb-1">
                  Ready to discuss scaffolding in your area?
                </h4>
                <p className="text-sm text-secondary-foreground/80 max-w-2xl">
                  Our contracts team can advise on permit lead times, staging schedules, and technical site requirements across the South East.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="/ContactRequestQuotePage">
                  Request a quote
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-transparent text-secondary-foreground border-white/20 hover:bg-white/10 hover:text-secondary-foreground">
                <Link to="/ProjectsCaseStudiesPage">
                  View past projects
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}